import { useCallback } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { CampLocation } from '@/types/campLocation';
import { Variation } from '@/components/admin/locations/types/variation';
import { OfferFormValues } from '../schemas/offerFormSchema';

interface UseVariationUtilsOptions {
  usePublishedData?: boolean;
}

/**
 * Hook that provides utility functions for working with variations (programs)
 * 
 * @param location The camp location containing variations
 * @param form The form instance from useForm
 * @param options Options for the hook
 */
export const useVariationUtils = (
  location: CampLocation,
  form: UseFormReturn<OfferFormValues>,
  options: UseVariationUtilsOptions = {}
) => {
  const { usePublishedData = true } = options;
  
  // Function to find a variation by key
  const findVariation = useCallback((variationKey: string): Variation | undefined => {
    if (!location || !location.variations) {
      return undefined;
    }
    
    return location.variations.find(v => v.key === variationKey);
  }, [location]);
  
  // Function to derive number of days from increment key
  const getNumDaysFromIncrementKey = useCallback((key: string | undefined): number | undefined => {
    if (!key) return undefined;
    
    // Extract days from increment key patterns
    const dayMatch = key.match(/oi_(\d+)day/i);
    if (dayMatch && dayMatch[1]) {
      return parseInt(dayMatch[1], 10);
    }
    
    // Extract weeks and convert to days
    const weekMatch = key.match(/oi_(\d+)week/i);
    if (weekMatch && weekMatch[1]) {
      return parseInt(weekMatch[1], 10) * 5; // Assuming 5 days per week
    }
    
    return undefined;
  }, []);
  
  // ENHANCED: Function to reset a field to its effective program value (base OR alternative)
  const resetFieldToProgram = useCallback((
    fieldName: string, 
    resetFieldModified?: (fieldName: string) => void,
    enumsMap?: Map<string, any>
  ) => {
    // Get current variation and session length
    const variation = form.getValues('variation');
    if (!variation) return;
    
    const numDaysOverride = form.getValues('numDaysOverride');
    
    // Reset to program value based on field name
    if (fieldName === 'priceOverride') {
      // CRITICAL FIX: Reset to effective program price (base OR alternative)
      let effectiveProgramPrice = variation.priceRegular || null;
      
      // Check if we should use alternative pricing based on current session length
      if (variation.regularOfferIncrementAlternatives && enumsMap && numDaysOverride) {
        for (const alternative of variation.regularOfferIncrementAlternatives) {
          const enumItem = enumsMap.get(alternative.key);
          if (enumItem && enumItem.value === numDaysOverride) {
            effectiveProgramPrice = alternative.price;
            break;
          }
        }
      }
      
      form.setValue('priceOverride', effectiveProgramPrice, {
        shouldDirty: true,
        shouldValidate: true
      });
    } 
    else if (fieldName === 'numDaysOverride') {
      let programNumDays = variation.numDays || null;
      
      // Try to derive from regularOfferIncrementKey if available and we have enumsMap
      if (enumsMap && variation.regularOfferIncrementKey && 
          (programNumDays === null || programNumDays === undefined)) {
        const enumEntry = enumsMap.get(variation.regularOfferIncrementKey);
        if (enumEntry && enumEntry.value) {
          programNumDays = enumEntry.value;
        }
      }
      
      form.setValue('numDaysOverride', programNumDays, {
        shouldDirty: true,
        shouldValidate: true
      });
    }
    else if (fieldName === 'priceNoteOverride') {
      // Note: This is handled by useProgramFieldManagement for alternative pricing
      // This base implementation only handles base program notes
      form.setValue('priceNoteOverride', variation.priceNote || '', {
        shouldDirty: true,
        shouldValidate: true
      });
    }
    else if (fieldName === 'timingNoteOverride') {
      form.setValue('timingNoteOverride', variation.timings?.timingNote || '', {
        shouldDirty: true,
        shouldValidate: true
      });
    }
    
    // Reset the modification state if a callback was provided
    if (resetFieldModified) {
      resetFieldModified(fieldName);
    }
  }, [form]);
  
  // Function to check if a field value is equal to its program value
  const isEqualToProgramValue = useCallback((
    fieldName: string, 
    currentValue: any,
    enumsMap?: Map<string, any>
  ): boolean => {
    // Get current variation
    const variation = form.getValues('variation');
    if (!variation) return false;
    
    // Compare based on field name
    if (fieldName === 'priceOverride') {
      return currentValue === variation.priceRegular;
    } 
    else if (fieldName === 'numDaysOverride') {
      // First check direct numDays property
      if (currentValue === variation.numDays) {
        return true;
      }
      
      // If no direct match and we have enumsMap, try with derived value
      if (enumsMap && variation.regularOfferIncrementKey) {
        const enumEntry = enumsMap.get(variation.regularOfferIncrementKey);
        if (enumEntry && enumEntry.value === currentValue) {
          return true;
        }
      }
      
      return false;
    }
    else if (fieldName === 'priceNoteOverride') {
      return currentValue === (variation.priceNote || '');
    }
    else if (fieldName === 'timingNoteOverride') {
      return currentValue === (variation.timings?.timingNote || '');
    }
    
    return false;
  }, [form]);
  
  return {
    findVariation,
    getNumDaysFromIncrementKey,
    resetFieldToProgram,
    isEqualToProgramValue
  };
};
